import { formatBytes } from "@saucer/core";
import type { StatsDay, StatsPair } from "@saucer/core";

/**
 * The pieces `/stats` and the admin platform view are both drawn from. They
 * live here rather than in either page so that one account's chart and the
 * whole platform's chart cannot drift apart.
 */

export type Metric = "opens" | "bytes";

export const RANGES = [
  { days: 7, label: "7d" },
  { days: 30, label: "30d" },
  { days: 90, label: "90d" },
] as const;

export function Tile({ label, value, hint }: { label: string; value: number | string; hint?: string }) {
  return (
    <div className="tile">
      <span className="tile__value">
        {typeof value === "number" ? value.toLocaleString() : value}
      </span>
      <span className="tile__label">
        {label}
        {hint && <span className="tile__hint"> {hint}</span>}
      </span>
    </div>
  );
}

export function Toggle({
  options,
  active,
  onPick,
}: {
  options: { key: string; label: string }[];
  active: string;
  onPick: (key: string) => void;
}) {
  return (
    <div className="toggle">
      {options.map((option) => (
        <button
          key={option.key}
          type="button"
          className={`toggle__btn${option.key === active ? " toggle__btn--on" : ""}`}
          aria-pressed={option.key === active}
          onClick={() => onPick(option.key)}
        >
          {option.label}
        </button>
      ))}
    </div>
  );
}

/**
 * Vertical columns rather than the old horizontal bar list: 90 days has to fit
 * without the page turning into a scroll. Views and downloads stack so the
 * split stays readable at one column per day.
 */
export function Chart({
  days,
  metric,
  pick,
  describe,
}: {
  days: StatsDay[];
  metric: Metric;
  /** What to plot, when it is not opens or bytes — the signup curve, say. */
  pick?: (day: StatsDay) => number;
  describe?: (day: StatsDay) => string;
}) {
  const value =
    pick ?? ((day: StatsDay) => (metric === "bytes" ? day.bytes : day.views + day.downloads));
  const peak = Math.max(1, ...days.map(value));

  return (
    <ol className="chart" style={{ "--chart-cols": days.length } as React.CSSProperties}>
      {days.map((day) => {
        const total = value(day);
        const height = (total / peak) * 100;
        // The lighter remainder is downloads; anything with its own accessor is
        // a single series and fills the column.
        const viewShare =
          pick || metric === "bytes" || total === 0 ? 100 : (day.views / total) * 100;
        return (
          <li
            key={day.day}
            className="chart__col"
            title={
              describe
                ? describe(day)
                : metric === "bytes"
                  ? `${day.day} · ${formatBytes(day.bytes)}`
                  : `${day.day} · ${day.views} views, ${day.downloads} downloads, ${day.uploads} uploads`
            }
          >
            <span className="chart__stack" style={{ blockSize: `${height}%` }}>
              <span className="chart__views" style={{ blockSize: `${viewShare}%` }} />
            </span>
          </li>
        );
      })}
    </ol>
  );
}

/** A labelled list where each row carries its own share of the leader. */
export function Meters({ rows, empty }: { rows: StatsPair[]; empty: string }) {
  if (rows.length === 0) return <p className="meta">{empty}</p>;
  const peak = Math.max(1, ...rows.map((row) => row.hits));
  return (
    <ul className="meters">
      {rows.map((row) => (
        <li key={row.label} className="meters__row">
          <span className="meters__label mono">{row.label}</span>
          <span className="meters__track">
            <span className="meters__fill" style={{ inlineSize: `${(row.hits / peak) * 100}%` }} />
          </span>
          <span className="meta meters__value">{row.hits.toLocaleString()}</span>
        </li>
      ))}
    </ul>
  );
}

export function Sparks({
  values,
  labels,
  caption,
}: {
  values: number[];
  labels: string[];
  caption: string;
}) {
  const peak = Math.max(1, ...values);
  return (
    <>
      <span className="sparks__caption">{caption}</span>
      <ol className="sparks">
        {values.map((count, index) => (
          <li key={index} className="sparks__cell" title={`${labels[index]} · ${count}`}>
            <span className="sparks__bar" style={{ blockSize: `${(count / peak) * 100}%` }} />
            <span className="sparks__label">{labels[index]}</span>
          </li>
        ))}
      </ol>
    </>
  );
}

export const HOUR_LABELS = Array.from({ length: 24 }, (_, hour) => String(hour).padStart(2, "0"));
export const DAY_LABELS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

/** ISO-3166 alpha-2 maps onto the regional indicator block by a fixed offset. */
export function flagFor(country: string): string {
  if (!/^[A-Za-z]{2}$/.test(country)) return "🌐";
  return String.fromCodePoint(
    ...[...country.toUpperCase()].map((letter) => 0x1f1e6 + letter.charCodeAt(0) - 65),
  );
}
